import { Link, usePage } from "@inertiajs/react"
import { Building2, FileText, MailPlus, MapPinned } from "lucide-react"

type City = {
  id: number
  name: string
  slug: string
  timezone: string
}

type Meeting = {
  id: number
  title: string
  published_at: string | null
  published: boolean
}

type Flash = {
  notice?: string
  alert?: string
}

type Props = {
  city: City
  meetings: Meeting[]
}

export default function CitiesShow({ city, meetings }: Props) {
  const page = usePage<{ flash?: Flash }>()
  const flash = page.props.flash

  return (
    <main className="ch-noise relative z-10 flex-grow selection:bg-[hsl(var(--ch-primary))] selection:text-white">
      <section id="subscribe" className="ch-container border-b border-[hsl(var(--ch-primary))] py-12 md:py-20">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
          <div className="border-b border-[hsl(var(--ch-primary))] pb-8 md:col-span-8 md:border-b-0 md:border-r md:pb-0 md:pr-8">
            <h1 className="ch-masthead mb-6 text-[hsl(var(--ch-primary))]">{city.name}: Your Neighborhood, Noted.</h1>
            <p className="ch-body-lg mb-6 max-w-3xl text-[hsl(var(--ch-muted))]">
              An independent record of civic affairs for residents of {city.name}. We track council decisions, zoning
              changes, budget movement, and local infrastructure developments so you can stay informed without reading
              every minute of every meeting.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-[hsl(var(--ch-primary))]">
              <span className="ch-label-caps border-b border-[hsl(var(--ch-primary))] pb-1">Serving {city.name}</span>
              <span className="h-1 w-1 bg-[hsl(var(--ch-primary))]" />
              <span className="ch-label-caps border-b border-[hsl(var(--ch-primary))] pb-1">Local Council Record</span>
            </div>
          </div>

          <div className="pt-2 md:col-span-4 md:pl-8">
            <div className="border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface))] p-8 text-center">
              <MailPlus className="mx-auto mb-4 h-9 w-9 text-[hsl(var(--ch-primary))]" />
              <h2 className="ch-headline-md mb-2 text-[hsl(var(--ch-primary))]">The {city.name} Dispatch</h2>
              <p className="mb-6 text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
                Daily updates from your city hall delivered directly to your inbox.
              </p>
              {flash?.notice ? (
                <p className="mb-4 border border-emerald-300 bg-emerald-50 px-3 py-2 text-left text-sm text-emerald-900">
                  {flash.notice}
                </p>
              ) : null}
              {flash?.alert ? (
                <p className="mb-4 border border-rose-300 bg-rose-50 px-3 py-2 text-left text-sm text-rose-900">
                  {flash.alert}
                </p>
              ) : null}
              <form action={`/cities/${city.slug}/subscriptions`} className="space-y-4" method="post">
                <input
                  name="authenticity_token"
                  type="hidden"
                  value={document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""}
                />
                <div className="text-left">
                  <label className="ch-label-caps mb-1 block text-[hsl(var(--ch-primary))]" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    className="w-full border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-3 text-sm text-[hsl(var(--ch-primary))] placeholder:text-[hsl(var(--ch-muted))] focus:outline-none"
                    id="email"
                    name="email"
                    placeholder="citizen@example.com"
                    required
                    type="email"
                  />
                </div>
                <button
                  className="ch-label-caps w-full border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-primary))] py-4 text-white transition-colors hover:bg-[hsl(var(--ch-bg))] hover:text-[hsl(var(--ch-primary))]"
                  type="submit"
                >
                  Receive Dispatch
                </button>
              </form>
            </div>
          </div>
        </div>

      </section>

      <section id="city-ledger" className="ch-container py-16">
        <div className="mb-6 flex flex-col gap-3 border-b-[3px] border-[hsl(var(--ch-primary))] pb-2 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="ch-headline-lg uppercase text-[hsl(var(--ch-primary))]">{city.name} Municipal Ledger</h2>
          <Link className="ch-label-caps underline text-[hsl(var(--ch-primary))]" href="/">
            View All Cities
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-0 border-l border-t border-[hsl(var(--ch-primary))] md:grid-cols-3">
          {meetings.slice(0, 6).map((meeting, index) => (
            <Link
              className={`flex flex-col border-b border-r border-[hsl(var(--ch-primary))] p-6 transition-colors hover:bg-[hsl(var(--ch-surface))] ${
                index % 2 == 1 ? "bg-[hsl(var(--ch-surface-low))]" : ""
              }`}
              href={`/cities/${city.slug}/reports/${meeting.id}-${meeting.title.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
              key={meeting.id}
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <span className="ch-label-caps text-[hsl(var(--ch-muted))]">Council Report</span>
                <span className="text-xs text-[hsl(var(--ch-muted))]">
                  {meeting.published_at ? new Date(meeting.published_at).toLocaleDateString() : "Undated"}
                </span>
              </div>

              <h3 className="ch-headline-md mb-3 text-[hsl(var(--ch-primary))]">{meeting.title}</h3>
              <p className="mb-6 flex-grow text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
                Published summaries and records for this meeting are available in the municipal archive.
              </p>

              <div className="mt-auto flex items-center justify-between border-t border-dashed border-[hsl(var(--ch-muted))] pt-4">
                <span className="ch-label-caps text-[hsl(var(--ch-primary))]">
                  Status: {meeting.published ? "Published" : "In Progress"}
                </span>
                <FileText className="h-5 w-5 text-[hsl(var(--ch-primary))]" />
              </div>

            </Link>
          ))}

          {meetings.length === 0 ? (
            <div className="col-span-full border-b border-r border-[hsl(var(--ch-primary))] p-8 text-[hsl(var(--ch-muted))]">
              No meetings published yet for {city.name}.
            </div>
          ) : null}
        </div>
      </section>

      <section className="border-y border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface-low))]">
        <div className="ch-container grid grid-cols-1 gap-8 py-12 md:grid-cols-3">
          <div className="border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-6">
            <MapPinned className="mb-4 h-8 w-8 text-[hsl(var(--ch-primary))]" />
            <h3 className="ch-headline-md mb-2 text-[hsl(var(--ch-primary))]">City Profile</h3>
            <p className="text-sm text-[hsl(var(--ch-muted))]">Local timezone: {city.timezone}</p>
          </div>
          <div className="border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-6">
            <Building2 className="mb-4 h-8 w-8 text-[hsl(var(--ch-primary))]" />
            <h3 className="ch-headline-md mb-2 text-[hsl(var(--ch-primary))]">Civic Archive</h3>
            <p className="text-sm text-[hsl(var(--ch-muted))]">Structured records from council meetings and municipal decisions.</p>
          </div>
          <div className="border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-6">
            <MailPlus className="mb-4 h-8 w-8 text-[hsl(var(--ch-primary))]" />
            <h3 className="ch-headline-md mb-2 text-[hsl(var(--ch-primary))]">Dispatch</h3>
            <p className="text-sm text-[hsl(var(--ch-muted))]">Email summaries with practical updates on what changed and why it matters.</p>
          </div>
        </div>
      </section>
    </main>
  )
}
